import { useState, useMemo } from 'react';
import {
  Eye, Pencil, Users, BarChart3, Trash2, Star,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { getGroupId, formatNumber } from '../utils';

// Sortable column header
function SortHeader({ label, field, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  return (
    <button onClick={() => onSort(field)} className="gm-sort-btn">
      {label}
      <span className="gm-sort-arrows">
        <ChevronUp
          size={12}
          strokeWidth={3}
          color={active && sortDir === 'asc' ? 'var(--admin-primary)' : '#d1d5db'}
        />
        <ChevronDown
          size={12}
          strokeWidth={3}
          color={active && sortDir === 'desc' ? 'var(--admin-primary)' : '#d1d5db'}
          style={{ marginTop: -2 }}
        />
      </span>
    </button>
  );
}

export default function GroupTable({
  groups,
  isDefault,
  processing,
  onView,
  onEdit,
  onUsers,
  onInstruments,
  onDelete,
}) {
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    const arr = [...groups];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'users':
          cmp = (b.userCount || 0) - (a.userCount || 0);
          break;
        case 'commission':
          cmp = (b.commission || 0) - (a.commission || 0);
          break;
        case 'created':
          cmp = new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          break;
        default:
          cmp = 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [groups, sortBy, sortDir]);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="gm-table-wrap">
      <table className="gm-table">
        <thead>
          <tr>
            <th>
              <SortHeader label="Name" field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </th>
            <th className="gm-col-desc">
              <span className="gm-th-label">Description</span>
            </th>
            <th>
              <SortHeader label="Users" field="users" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </th>
            <th className="gm-col-comm">
              <SortHeader label="Commission" field="commission" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </th>
            <th className="gm-col-lev">
              <span className="gm-th-label">Leverage</span>
            </th>
            <th>
              <span className="gm-th-label">Status</span>
            </th>
            <th style={{ textAlign: 'right' }}>
              <span className="gm-th-label">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((group) => {
            const id = getGroupId(group);
            const def = isDefault(group);
            const forexLev = group.leverageSettings?.forex?.currentLeverage
              || group.leverageSettings?.stocks?.currentLeverage;
            const commissionPct = formatNumber((group.commission || 0) * 100, 4);

            return (
              <tr key={id} onClick={() => onView(id)}>
                {/* Name */}
                <td>
                  <div className="gm-td-name">
                    <span className="gm-td-name-text">{group.name}</span>
                    {def && (
                      <span className="gm-badge-default">
                        <Star size={10} fill="#f59e0b" color="#f59e0b" />
                        Default
                      </span>
                    )}
                  </div>
                </td>

                {/* Description */}
                <td className="gm-col-desc">
                  <p className="gm-td-desc">{group.description || '\u2014'}</p>
                </td>

                {/* Users */}
                <td>
                  <div className="gm-td-users">
                    <Users size={14} color="var(--admin-text-light)" />
                    <span>{group.userCount || 0}</span>
                  </div>
                </td>

                {/* Commission */}
                <td className="gm-col-comm">
                  <span className="gm-td-commission">{commissionPct}%</span>
                </td>

                {/* Leverage */}
                <td className="gm-col-lev">
                  <div className="gm-td-leverage">
                    <BarChart3 size={14} color="var(--admin-text-light)" />
                    <span>{forexLev || 1}:1</span>
                  </div>
                </td>

                {/* Status */}
                <td>
                  <span className={`gm-badge ${group.isActive !== false ? 'gm-badge-active' : 'gm-badge-inactive'}`}>
                    <span className={`gm-badge-dot ${group.isActive !== false ? 'gm-badge-dot-active' : 'gm-badge-dot-inactive'}`} />
                    {group.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>

                {/* Actions */}
                <td>
                  <div
                    className="gm-row-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button onClick={() => onView(id)} className="gm-action-btn gm-action-btn-view" title="View Details">
                      <Eye size={16} />
                    </button>
                    <button onClick={() => onEdit(id)} className="gm-action-btn gm-action-btn-edit" title="Edit Group">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => onUsers(id, group.name)} className="gm-action-btn gm-action-btn-users" title="Manage Users">
                      <Users size={16} />
                    </button>
                    <button onClick={() => onInstruments(id, group.name)} className="gm-action-btn gm-action-btn-inst" title="Manage Instruments">
                      <BarChart3 size={16} />
                    </button>
                    {!def && (
                      <button
                        onClick={() => onDelete(id, group.name)}
                        disabled={processing}
                        className="gm-action-btn gm-action-btn-delete"
                        title="Delete Group"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
